import { ButtonHTMLAttributes, ReactNode } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
}

/**
 * Reusable button component with mobile-first design
 * Meets FR-047: 44x44px minimum touch target size
 */
export function Button({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled,
  className = '',
  style,
  ...props
}: ButtonProps) {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontWeight: 600,
    border: 'none',
    borderRadius: '12px', // More rounded corners
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    width: fullWidth ? '100%' : 'auto',
    // FR-047: Minimum 44x44px touch target
    minHeight: '44px',
    minWidth: size === 'small' ? '120px' : size === 'large' ? '180px' : '140px', // Better minimum width
    padding: size === 'small' ? '10px 20px' : size === 'large' ? '14px 32px' : '12px 28px', // Better padding
    fontSize: size === 'small' ? '14px' : size === 'large' ? '18px' : '16px',
  }

  const variantStyles = {
    primary: {
      backgroundColor: disabled ? '#ccc' : '#2563eb', // blue-600
      color: '#fff',
    },
    secondary: {
      backgroundColor: disabled ? '#f0f0f0' : '#f5f5f5',
      color: disabled ? '#999' : '#333',
    },
    outline: {
      backgroundColor: 'transparent',
      color: disabled ? '#ccc' : '#2563eb',
      border: `2px solid ${disabled ? '#ccc' : '#2563eb'}`,
    },
  }

  const hoverStyles =
    !disabled &&
    (variant === 'primary'
      ? { backgroundColor: '#1d4ed8' } // blue-700
      : variant === 'secondary'
        ? { backgroundColor: '#e8e8e8' }
        : { backgroundColor: 'rgba(37, 99, 235, 0.1)' })

  return (
    <button
      style={{
        ...baseStyles,
        ...variantStyles[variant],
        ...style, // Merge inline styles last to allow overrides
      }}
      onMouseEnter={e => {
        if (!disabled && hoverStyles) {
          Object.assign(e.currentTarget.style, hoverStyles)
        }
      }}
      onMouseLeave={e => {
        Object.assign(e.currentTarget.style, { ...variantStyles[variant], ...style })
      }}
      disabled={disabled}
      className={className}
      {...props}
    >
      {children}
    </button>
  )
}
